import DOMPurify from "isomorphic-dompurify"
import parse from "html-react-parser"
import { GetStaticPaths, GetStaticProps } from "next"
import SkinuvitaSeoHead from "../../src/components/LandingPage/SkinuvitaSeoHead"
import Link from "next/link"
import { useTranslations } from "next-intl"
import Footer from "src/components/footer"
import NavBarPatients from "src/components/LandingPage/navbars/NavBarPatients"
import StyledText from "src/typography/typography-component"
import { JobListingPage, Skill } from "src/types/jobs"
import {
  fetchJobListingBySlug,
  fetchJobListings,
  formatJobDeadline,
  formatJobSalary,
  getJobEmploymentTypeLabel,
  getJobListingHref,
  getJobsPageHref,
  getJobWorkModeLabel,
  hasRichTextContent,
  REVALIDATE_INTERVAL,
} from "src/utils/jobs"
import skColors from "styles/skColors"
import styles from "styles/LandingPage/careers.module.scss"
import Image from "next/image"
import BehindNavBar from "../../src/components/BehindNavBar/BehindNavBar"
import ChipComponent from "../../src/komm-ins-team/components/ChipComponent"
import { Divider } from "@mui/material"

type JobDetailPageProps = {
  job: JobListingPage
  locale: string
}

function RichTextSection({
  title,
  html,
  className,
}: {
  title: string
  html: string | null | undefined
  className?: string
}) {
  if (!hasRichTextContent(html)) {
    return null
  }

  return (
    <section className={`${styles.richTextCardNoBackground} ${className || ""}`}>
      <StyledText variant="h3" className={styles.sectionHeading} color={skColors.turquoise[600]}>
        {title}
      </StyledText>
      <div className={styles.richText}>
        <StyledText variant={"pLarge"} as={"span"}>
          {parse(DOMPurify.sanitize(html || ""))}
        </StyledText>
      </div>
    </section>
  )
}

export default function JobDetailPage({ job, locale }: JobDetailPageProps) {
  const t = useTranslations("careers")
  const employmentType = getJobEmploymentTypeLabel(job.employment_type, locale)
  const workMode = getJobWorkModeLabel(job.work_mode, locale)
  const salary = formatJobSalary(job, locale)
  const deadline = formatJobDeadline(job.application_deadline, locale)
  const applicationHref = job.description_external_url || (job.contact_email ? `mailto:${job.contact_email}` : null)
  const departmentImage = job.department?.image
  const hasSkills = job.required_skills.length > 0 || job.nice_to_have_skills.length > 0
  const hasSidebarDetails = job.departments.length > 0 || Boolean(job.experience_level) || job.tags.length > 0
  const skillLabels =
    locale === "de"
      ? {
          heading: "Skills",
          required: "Erforderlich",
          niceToHave: "Nice to have",
          none: "Keine Skills hinterlegt",
        }
      : {
          heading: "Skills",
          required: "Required",
          niceToHave: "Nice to have",
          none: "No skills provided",
        }

  const factItems = [
    { label: t("detail.labels.location"), value: job.location },
    { label: t("detail.labels.employmentType"), value: employmentType },
    { label: t("detail.labels.workMode"), value: workMode },
    { label: t("detail.labels.salary"), value: salary },
  ].filter((item) => item.value)

  const applicationItems = [
    { label: t("detail.contact"), value: job.contact_email },
    { label: t("detail.deadline"), value: deadline },
  ].filter((item) => item.value)

  const getColorsForSkill = (skill: Skill) => {
    let backgroundColor
    let color
    switch (skill.category) {
      case "certifications":
        backgroundColor = skColors.brightgreen[200]
        color = skColors.brightgreen[600]
        break
      case "languages":
        backgroundColor = skColors.darkgreen[300]
        color = skColors.darkgreen[600]
        break
      case "soft_skills":
        backgroundColor = skColors.yellow[300]
        color = skColors.blue[600]
        break
      case "technical":
        backgroundColor = skColors.blue[300]
        color = skColors.blue[600]
        break
      case "tools":
        backgroundColor = skColors.monochrome[200]
        color = skColors.blue[600]
        break
    }
    return {color, backgroundColor}
  }
  return (
    <div className={styles.detailPage}>
      <SkinuvitaSeoHead
        title={`${job.title} | ${t("meta.title")}`}
        description={job.summary || t("meta.description")}
        canonicalPath={getJobListingHref(job.meta.slug)}
      />

      <NavBarPatients />
      <BehindNavBar />
      <section className={`${styles.detailHero} ${styles.sectionAfter}`}>
        <div className={styles.heroInner}>
          <Link href={getJobsPageHref()} className={styles.backLink}>
            <div className={styles.backButton}>
              <Image src={"/svg/arrows.svg"} alt={"back to listings"} width={16} height={16} />
            </div>
          </Link>
          <div className={styles.detailHeroTop}>
            <div>
              {/*<div className={styles.jobMeta}>*/}
              {/*  {job.location && (*/}
              {/*    <span className={styles.pill}>*/}
              {/*      <StyledText variant={"tags"}>{job.location}</StyledText>*/}
              {/*    </span>*/}
              {/*  )}*/}
              {/*  {employmentType && (*/}
              {/*    <span className={`${styles.pill} ${styles.pillMuted}`}>*/}
              {/*      <StyledText variant={"tags"}>{employmentType}</StyledText>*/}
              {/*    </span>*/}
              {/*  )}*/}
              {/*  {workMode && (*/}
              {/*    <span className={`${styles.pill} ${styles.pillMuted}`}>*/}
              {/*      <StyledText variant={"tags"}>{workMode}</StyledText>*/}
              {/*    </span>*/}
              {/*  )}*/}
              {/*</div>*/}

              <StyledText variant="h1" className={styles.detailTitle} color={skColors.turquoise[600]}>
                {job.title}
              </StyledText>

              {job.summary && (
                <StyledText variant="pLarge" className={styles.detailSummary} color={skColors.darkgreen[600]}>
                  {job.summary}
                </StyledText>
              )}

              {/*{applicationHref && (*/}
              {/*  <div className={styles.detailActions}>*/}
              {/*    <a*/}
              {/*      href={applicationHref}*/}
              {/*      className={styles.inlineCta}*/}
              {/*      target={job.description_external_url ? "_blank" : undefined}*/}
              {/*      rel={job.description_external_url ? "noreferrer" : undefined}*/}
              {/*    >*/}
              {/*      {job.description_external_url ? t("detail.external") : t("detail.email")}*/}
              {/*    </a>*/}
              {/*  </div>*/}
              {/*)}*/}
            </div>

            {departmentImage && (
              <div className={styles.departmentImageWrap}>
                <img
                  src={departmentImage.full_url || departmentImage.url}
                  alt={job.department?.name || job.title}
                  className={styles.departmentImage}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <main className={styles.detailMain}>
        {factItems.length > 0 && (
          <section className={`${styles.factGrid}`}>
            {factItems.map((item) => (
              <article key={item.label} className={styles.factCard}>
                <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                  {item.label}
                </StyledText>
                <StyledText variant="pRegular" className={styles.applicationValue} color={skColors.darkgreen[600]}>
                  {item.value}
                </StyledText>
              </article>
            ))}
          </section>
        )}

        {/*{job.summary && (*/}
        {/*  <section className={styles.richTextCardNoBackground}>*/}
        {/*    <StyledText variant="h3" className={styles.sectionHeading} color={skColors.darkgreen[600]}>*/}
        {/*      {t("detail.summary")}*/}
        {/*    </StyledText>*/}
        {/*    <StyledText variant="pLarge" color={skColors.darkgreen[600]}>*/}
        {/*      {job.summary}*/}
        {/*    </StyledText>*/}
        {/*  </section>*/}
        {/*)}*/}

        <section className={`${styles.detailContentGrid} `}>
          <RichTextSection title={t("detail.aboutRole")} html={job.about_role} />

          <section className={styles.richTextCardNoBackground}>
            <StyledText variant="h3" className={styles.sectionHeading} color={skColors.turquoise[600]}>
              {skillLabels.heading}
            </StyledText>

            {hasSkills ? (
              <div className={styles.skillsBlock}>
                {job.required_skills.length > 0 && (
                  <div>
                    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                      {skillLabels.required}
                    </StyledText>
                    <div className={styles.departments}>
                      {job.required_skills.map((skill) => {
                        const { color, backgroundColor } = getColorsForSkill(skill)

                        return (
                          <ChipComponent key={`required-${skill.id}`} bgColor={backgroundColor} color={color}>
                            {skill.name || skill.slug || `#${skill.id}`}
                          </ChipComponent>
                        )
                      })}
                    </div>
                  </div>
                )}

                {job.nice_to_have_skills.length > 0 && (
                  <div>
                    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                      {skillLabels.niceToHave}
                    </StyledText>
                    <div className={styles.departments}>
                      {job.nice_to_have_skills.map((skill) => {
                        const { color, backgroundColor } = getColorsForSkill(skill)
                        return <ChipComponent key={`nice-${skill.id}`} bgColor={backgroundColor} color={color}>
                          {skill.name || skill.slug || `#${skill.id}`}
                        </ChipComponent>
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <StyledText variant="pRegular" color={skColors.darkgreen[600]}>
                {skillLabels.none}
              </StyledText>
            )}

            {hasSidebarDetails && (
              <div className={styles.skillsBlock}>
                {/*{job.departments.length > 0 && (*/}
                {/*  <div>*/}
                {/*    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>*/}
                {/*      {locale === "de" ? "Abteilungen" : "Departments"}*/}
                {/*    </StyledText>*/}
                {/*    <div className={styles.departments}>*/}
                {/*      {job.departments.map((department) => (*/}
                {/*        <span key={department.id} className={`${styles.pill} ${styles.pillMuted}`}>*/}
                {/*          {department.name}*/}
                {/*        </span>*/}
                {/*      ))}*/}
                {/*    </div>*/}
                {/*  </div>*/}
                {/*)}*/}

                {job.experience_level && (
                  <div>
                    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                      {locale === "de" ? "Erfahrungslevel" : "Experience level"}
                    </StyledText>
                    <StyledText variant="pRegular" color={skColors.darkgreen[600]}>
                      {job.experience_level.name}
                    </StyledText>
                  </div>
                )}

                {job.tags.length > 0 && (
                  <div>
                    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                      Tags
                    </StyledText>
                    <div className={styles.departments}>
                      {job.tags.map((tag) => (
                        <span key={tag} className={styles.pill}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </section>
        <Divider style={{ border: `1px solid ${skColors.turquoise[400]}` }} />
        <RichTextSection
          className={styles.sectionAfter}
          title={t("detail.responsibilities")}
          html={job.responsibilities}
        />
        <RichTextSection className={styles.sectionAfter} title={t("detail.requirements")} html={job.requirements} />
        <RichTextSection className={styles.sectionAfter} title={t("detail.niceToHave")} html={job.nice_to_have} />
        <RichTextSection className={styles.sectionAfter} title={t("detail.benefits")} html={job.benefits} />

        {(applicationItems.length > 0 || applicationHref) && (
          <section className={styles.applicationCard}>
            <StyledText variant="h3" className={styles.sectionHeading} color={skColors.darkgreen[600]}>
              {t("detail.application")}
            </StyledText>

            {applicationItems.length > 0 && (
              <div className={styles.applicationGrid}>
                {applicationItems.map((item) => (
                  <article key={item.label} className={styles.factCard}>
                    <StyledText variant="captions" className={styles.applicationLabel} color={skColors.turquoise[600]}>
                      {item.label}
                    </StyledText>
                    <StyledText variant="pRegular" className={styles.applicationValue} color={skColors.darkgreen[600]}>
                      {item.value}
                    </StyledText>
                  </article>
                ))}
              </div>
            )}

            {applicationHref && (
              <div className={styles.detailActions}>
                <a
                  href={applicationHref}
                  className={styles.inlineCta}
                  target={job.description_external_url ? "_blank" : undefined}
                  rel={job.description_external_url ? "noreferrer" : undefined}
                >
                  {job.description_external_url ? t("detail.external") : t("detail.email")}
                </a>
              </div>
            )}
          </section>
        )}
        <section className={styles.richTextCardNoBackground}>
          <StyledText color={skColors.turquoise[600]} variant={"h3"} className={styles.sectionHeading}>
            {t("interested.title")}
          </StyledText>
          <StyledText variant={"pLarge"}>
            {t.rich("interested.description", {
              mail: (chunk) => (
                <a className={styles.email} href={"mailto:hr@skinuvita.de"}>
                  {chunk}
                </a>
              ),
            })}
          </StyledText>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const response = await fetchJobListings()

    return {
      paths: response.items.map((job) => ({
        params: {
          slug: job.meta.slug,
        },
      })),
      fallback: "blocking",
    }
  } catch (error) {
    console.error("Failed to fetch job paths", error)

    return {
      paths: [],
      fallback: "blocking",
    }
  }
}

export const getStaticProps: GetStaticProps<JobDetailPageProps> = async (context) => {
  const locale = (context.locale as string) || "de"
  const slug = context.params?.slug as string

  let messages
  try {
    messages = (await import(`../../src/intl/${locale}.json`)).default
  } catch (error) {
    messages = (await import(`../../src/intl/de.json`)).default
  }

  try {
    const job = await fetchJobListingBySlug(slug)

    if (!job) {
      return {
        notFound: true,
      }
    }

    return {
      revalidate: REVALIDATE_INTERVAL,
      props: {
        job,
        messages,
        locale,
      },
    }
  } catch (error) {
    console.error(`Failed to fetch job listing for slug ${slug}`, error)

    return {
      notFound: true,
    }
  }
}
